export interface EstensaFault {
    createdOn: string;
    exceptionMessage: string;
    exceptionTypeName: string;
    httpStatusCode: number;
    id: string;
    estensaFaultDetails?: IEstensaFaultDetails;
}

export interface IEstensaFaultDetails {
    estensaFault: IEstensaFaultModel;
    typeName: string;
}

export interface IEstensaFaultModel {
    faultType?: string;
    message?: string;
    completeMessage?: string;
    errorId?: string;
    disableErrorMsgBoxOnClient?: boolean;
}

export function isExtensaFault(ex: unknown): ex is EstensaFault {
    return typeof ex === 'object' && !!ex && 'exceptionTypeName' in ex && typeof ex.exceptionTypeName === 'string';
}

//Custom types

//Lock Faults

const lockFaultTypeNames = ['Estensa.Web.Exceptions.LockedFault', 'Estensa.Web.Exceptions.UnlockedFault'] as const;

export interface EstensaLockFaultModel extends IEstensaFaultModel {
    userFullName: string;
}

export interface EstensaLockFaultDetails extends IEstensaFaultDetails {
    estensaFault: EstensaLockFaultModel;
}

export interface EstensaLockFault extends EstensaFault {
    exceptionTypeName: (typeof lockFaultTypeNames)[number];
    estensaFaultDetails: EstensaLockFaultDetails;
}

export function isExtensaLockFault(ex: unknown): ex is EstensaLockFault {
    return isExtensaFault(ex) && lockFaultTypeNames.includes(ex.exceptionTypeName as any);
}

//Merge Faults

const mergeFaultTypeName = 'Estensa.Web.Exceptions.DetailCreatedWhileReportingFault' as const;
export interface EstensaMergeFault extends EstensaFault {
    exceptionTypeName: typeof mergeFaultTypeName;
}

export function isMergeFault(ex: unknown): ex is EstensaMergeFault {
    return isExtensaFault(ex) && ex.exceptionTypeName === mergeFaultTypeName;
}

//Too Much Result Faults

const tooMuchResultFaultTypeName = 'Estensa.Web.Exceptions.TooMuchResultFault' as const;

export interface EstensaTooMuchResultFaultModel extends IEstensaFaultModel {
    count: number;
    limit: number;
}

export interface EstensTooMuchResultFaultDetails extends IEstensaFaultDetails {
    estensaFault: EstensaTooMuchResultFaultModel;
}
export interface EstensaTooMuchResultFault extends EstensaFault {
    exceptionTypeName: typeof tooMuchResultFaultTypeName;
    estensaFaultDetails: EstensTooMuchResultFaultDetails;
}

export function isTooMuchResultFault(ex: unknown): ex is EstensaTooMuchResultFault {
    return isExtensaFault(ex) && ex.exceptionTypeName === tooMuchResultFaultTypeName;
}

//Mandatory Equipment Change Faults

const mandatoryEquipmentChangeFaultExceptionTypeName = 'Estensa.Web.Exceptions.MandatoryEquipmentChangeFault' as const;
const mandatoryEquipmentChangeFaultTypeName = 'MandatoryEquipmentChangeFault' as const;
export interface EstensaMandatoryEquipmentChangeFault extends EstensaFault {
    exceptionTypeName: typeof mandatoryEquipmentChangeFaultExceptionTypeName;
    estensaFaultDetails: {
        estensaFault: {
            idExaminations: string[];
            idEquipment: string;
            faultType: typeof mandatoryEquipmentChangeFaultExceptionTypeName;
            message: string;
            completeMessage: string;
            errorId: string;
            disableErrorMsgBoxOnClient: boolean;
        };
        typeName: typeof mandatoryEquipmentChangeFaultTypeName;
    };
}

export function isMandatoryEquipmentChangeFault(ex: unknown): ex is EstensaMandatoryEquipmentChangeFault {
    return (
        isExtensaFault(ex) &&
        ex.exceptionTypeName === mandatoryEquipmentChangeFaultExceptionTypeName &&
        'estensaFaultDetails' in ex &&
        typeof ex.estensaFaultDetails === 'object' &&
        !!ex.estensaFaultDetails &&
        'typeName' in ex.estensaFaultDetails &&
        ex.estensaFaultDetails.typeName === mandatoryEquipmentChangeFaultTypeName
    );
}

//Key Image Faults

const keyImageNotFoundFaultExceptionTypeName = 'Estensa.Web.Exceptions.KeyImageNotFoundFault' as const;
const keyImageNotFoundFaultFaultTypeName = 'KeyImageNotFoundFault' as const;

export interface KeyImageNotFoundFaultDetails extends IEstensaFaultDetails {
    typeName: typeof keyImageNotFoundFaultExceptionTypeName;
}

export interface KeyImageNotFoundFault extends EstensaFault {
    exceptionTypeName: typeof keyImageNotFoundFaultFaultTypeName;
    estensaFaultDetails: KeyImageNotFoundFaultDetails;
}

export function isKeyImageNotFoundFault(ex: unknown): ex is KeyImageNotFoundFault {
    return (
        isExtensaFault(ex) &&
        ex.exceptionTypeName === keyImageNotFoundFaultExceptionTypeName &&
        'estensaFaultDetails' in ex &&
        typeof ex.estensaFaultDetails === 'object' &&
        !!ex.estensaFaultDetails &&
        'typeName' in ex.estensaFaultDetails &&
        ex.estensaFaultDetails.typeName === keyImageNotFoundFaultFaultTypeName
    );
}

//DeleteReportedExaminationsFault

const deleteReportedExaminationsFaultTypeName = 'Estensa.Web.Exceptions.DeleteReportedExaminationsFault' as const;

export interface DeleteReportedExaminationsFaultModel extends IEstensaFaultModel {
    idExaminations: string[];
}

export interface DeleteReportedExaminationsFaultDetails extends IEstensaFaultDetails {
    estensaFault: DeleteReportedExaminationsFaultModel;
}
export interface DeleteReportedExaminationsFault extends EstensaFault {
    exceptionTypeName: typeof deleteReportedExaminationsFaultTypeName;
    estensaFaultDetails: DeleteReportedExaminationsFaultDetails;
}

export function isDeleteReportedExaminationsFault(ex: unknown): ex is DeleteReportedExaminationsFault {
    return isExtensaFault(ex) && ex.exceptionTypeName === deleteReportedExaminationsFaultTypeName;
}
